import {
  FUNCTION_FLOW_IN_PORT_ID,
  FUNCTION_FLOW_OUT_PORT_ID,
} from "../../function-graph.js";
import { ControlNode } from "../../types.js";
import {
  BehaviorFlow,
  ExecutionContext,
  NodeExecutionResult,
  RuntimeSignal,
  ScheduledNode,
} from "../types.js";
import {
  coerceToBoolean,
  emptyPatchResult,
  resolveFramePortValue,
  toFiniteNumber,
} from "../helpers.js";
import { BehaviorNode } from "./BehaviorNode.js";

export class ControlBehaviorNode extends BehaviorNode {
  declare behavior: ControlNode;

  constructor(behavior: ControlNode) {
    super(behavior);
  }

  execute(context: ExecutionContext): NodeExecutionResult {
    const patch = emptyPatchResult();
    const scheduledNodes: ScheduledNode[] = [];
    const visiting: Set<string> = new Set();
    const nextFlows: BehaviorFlow[] = [];
    const runtimeSignals: RuntimeSignal[] = [];

    switch (this.behavior.controlType) {
      case "ifElse": {
        const raw = this.getInput("condition", visiting, context);
        const branch = coerceToBoolean(raw) ? "true" : "false";
        nextFlows.push(
          ...(this.outgoingEdgesByPort.get(branch) ?? []).map((edge) => ({
            edge,
            context,
          })),
        );
        if (nextFlows.length === 0 && context.loopStack.length > 0) {
          runtimeSignals.push({ type: "loop_continue", context: context });
        }
        break;
      }

      case "repeat": {
        const countRaw = this.getInput("count", visiting, context);
        const count = Math.max(0, Math.floor(toFiniteNumber(countRaw, 0)));
        const currentLoop = context.loopStack[context.loopStack.length - 1];
        const isCurrentLoop = currentLoop?.nodeId === this.behavior.id;
        if (!isCurrentLoop) {
          if (count <= 0) {
            nextFlows.push(
              ...(this.outgoingEdgesByPort.get("done") ?? []).map((edge) => ({
                edge,
                context,
              })),
            );
            if (nextFlows.length === 0) {
              if (context.loopStack.length > 0) {
                runtimeSignals.push({
                  type: "loop_continue",
                  context: context,
                });
              } else {
                runtimeSignals.push({
                  type: "loop_done",
                  context: context,
                  repeatNodeId: this.behavior.id,
                });
              }
            }
          } else {
            const nextContext = {
              ...context,
              loopStack: [
                ...context.loopStack,
                { nodeId: this.behavior.id, index: 0 },
              ],
            };
            nextFlows.push(
              ...(this.outgoingEdgesByPort.get("body") ?? []).map((edge) => ({
                edge,
                context: nextContext,
              })),
            );
          }
        } else if (currentLoop) {
          const nextIndex = currentLoop.index + 1;
          if (nextIndex < count) {
            const nextLoopStack = [...context.loopStack];
            nextLoopStack[nextLoopStack.length - 1] = {
              nodeId: this.behavior.id,
              index: nextIndex,
            };
            nextFlows.push(
              ...(this.outgoingEdgesByPort.get("body") ?? []).map((edge) => ({
                edge,
                context: { ...context, loopStack: nextLoopStack },
              })),
            );
          } else {
            const nextContext = {
              ...context,
              loopStack: [...context.loopStack.slice(0, -1)],
            };
            nextFlows.push(
              ...(this.outgoingEdgesByPort.get("done") ?? []).map((edge) => ({
                edge,
                context: nextContext,
              })),
            );
            if (nextFlows.length === 0) {
              if (nextContext.loopStack.length > 0) {
                runtimeSignals.push({
                  type: "loop_continue",
                  context: nextContext,
                });
              } else {
                runtimeSignals.push({
                  type: "loop_done",
                  context: nextContext,
                  repeatNodeId: this.behavior.id,
                });
              }
            }
          }
        }
        break;
      }
      case "wait": {
        const delayRaw = this.getInput("delayMs", visiting, context);
        const delayMs = Math.max(0, Math.floor(toFiniteNumber(delayRaw, 0)));

        const outEdges = this.outgoingEdgesByPort.get("out");
        if (outEdges) {
          outEdges.forEach((edge) => {
            scheduledNodes.push({
              at: context.nowMs + delayMs,
              kind: "edge",
              edge: edge,
              context: context,
            });
          });
        } else if (context.loopStack.length > 0) {
          scheduledNodes.push({
            at: context.nowMs + delayMs,
            kind: "signal",
            signal: { type: "loop_continue", context: context },
            context: context,
          });
        }
        break;
      }
      case "mergeFlows": {
        nextFlows.push(
          ...(this.outgoingEdgesByPort.get("out") ?? []).map((edge) => ({
            edge,
            context,
          })),
        );
        if (nextFlows.length === 0 && context.loopStack.length > 0) {
          runtimeSignals.push({ type: "loop_continue", context });
        }
        break;
      }
      case "eventOutput": {
        runtimeSignals.push({ type: "event_output_reached", context });
        break;
      }
      case "functionBegin": {
        nextFlows.push(
          ...(this.outgoingEdgesByPort.get("out") ?? []).map((edge) => ({
            edge,
            context,
          })),
        );
        if (nextFlows.length === 0 && context.loopStack.length > 0) {
          runtimeSignals.push({ type: "loop_continue", context });
        }
        break;
      }
      case "variableBegin":
      case "variableEnd":
        break;
      case "functionEnd": {
        const returnStack = context.functionReturnStack ?? [];
        const visiting = new Set<string>();
        const outputs: Record<string, unknown> = {};
        for (const port of this.behavior.ports) {
          if (
            port.direction === "input" &&
            port.id !== "in" &&
            port.valueType !== "flow"
          ) {
            outputs[port.id] = this.getInput(port.id, visiting, context);
          }
        }

        if (returnStack.length > 0) {
          const returnFrame = returnStack[returnStack.length - 1];
          const triggeredEndPort =
            context.triggeredInputFlowPort ?? FUNCTION_FLOW_IN_PORT_ID;
          const resumePortId =
            triggeredEndPort === FUNCTION_FLOW_IN_PORT_ID
              ? FUNCTION_FLOW_OUT_PORT_ID
              : triggeredEndPort;
          const resumeFlows = returnFrame?.flowsByPort[resumePortId] ?? [];
          const frameStack = context.functionCallFrameStack ?? [];
          const nextContext: ExecutionContext = {
            ...context,
            functionReturnStack: returnStack.slice(0, -1),
            functionCallFrameStack: frameStack.slice(0, -1),
            functionCallOutputs: outputs,
          };
          nextFlows.push(
            ...resumeFlows.map((flow) => ({
              ...flow,
              context: nextContext,
            })),
          );
          if (nextFlows.length === 0 && context.loopStack.length > 0) {
            runtimeSignals.push({
              type: "loop_continue",
              context: nextContext,
            });
          }
        } else {
          runtimeSignals.push({ type: "function_end_reached", context });
        }
        break;
      }
    }
    return {
      patch,
      nextFlows,
      scheduledNodes,
      runtimeSignals,
    };
  }

  getOutput(
    portId: string,
    _visiting: Set<string>,
    context: ExecutionContext,
    binding?: boolean,
  ): unknown {
    if (this.behavior.controlType === "functionBegin" && portId !== "out") {
      const frameStack = context.functionCallFrameStack;
      const frame = frameStack?.[frameStack.length - 1];
      const value = frame?.inputs?.[portId];
      return resolveFramePortValue(value, context.data, binding === true);
    }
    if (this.behavior.controlType === "variableBegin") {
      const frameStack = context.variableInputFrameStack;
      const frame = frameStack?.[frameStack.length - 1];
      const value = frame?.inputs?.[portId];
      return resolveFramePortValue(value, context.data, binding === true);
    }
    if (this.behavior.controlType === "repeat" && portId === "loopIndex") {
      for (let i = context.loopStack.length - 1; i >= 0; i -= 1) {
        const loop = context.loopStack[i];
        if (loop?.nodeId === this.behavior.id) {
          return loop.index;
        }
      }
      return undefined;
    }
    return undefined;
  }
}
